import { DEFAULT_SUPPLIER_REQUEST_STATUSES, SUPPLIER_REQUEST_STATUS } from './constants';
import type { SupplierRequestFilterState } from './types';

const storageKey = (userId: number): string =>
    `voltox-${userId}-supplier-requests-filter`;

const ALLOWED_STATUSES = new Set<number>([
    SUPPLIER_REQUEST_STATUS.ACTIVE,
    SUPPLIER_REQUEST_STATUS.DONE,
    SUPPLIER_REQUEST_STATUS.DELETED,
]);

export const defaultSupplierRequestFilter = (): SupplierRequestFilterState => ({
    vendors: [],
    statuses: [...DEFAULT_SUPPLIER_REQUEST_STATUSES],
});

const normalizeFilter = (value: unknown): SupplierRequestFilterState | null => {
    if (!value || typeof value !== 'object') return null;
    const row = value as Record<string, unknown>;
    const vendors = Array.isArray(row.vendors)
        ? row.vendors.map(String).filter(Boolean)
        : [];
    const statuses = Array.isArray(row.statuses)
        ? row.statuses
              .map(Number)
              .filter((status) => Number.isFinite(status) && ALLOWED_STATUSES.has(status))
        : [];

    return {
        vendors,
        statuses: statuses.length ? statuses : [...DEFAULT_SUPPLIER_REQUEST_STATUSES],
    };
};

export const loadSupplierRequestFilter = (
    userId: number,
): SupplierRequestFilterState => {
    if (!userId || typeof window === 'undefined') return defaultSupplierRequestFilter();
    try {
        const raw = window.localStorage.getItem(storageKey(userId));
        if (!raw) return defaultSupplierRequestFilter();
        return normalizeFilter(JSON.parse(raw)) || defaultSupplierRequestFilter();
    } catch {
        return defaultSupplierRequestFilter();
    }
};

export const saveSupplierRequestFilter = (
    userId: number,
    filter: SupplierRequestFilterState | null | undefined,
): void => {
    if (!userId || typeof window === 'undefined') return;
    try {
        const normalized = normalizeFilter(filter);
        if (!normalized) {
            window.localStorage.removeItem(storageKey(userId));
            return;
        }
        window.localStorage.setItem(storageKey(userId), JSON.stringify(normalized));
    } catch {
        /* ignore quota / private mode */
    }
};
